export interface ThemeColors {
  // Primaire
  primary:        string;
  primaryHover:   string;
  primaryLight:   string;
  primaryDark:    string;
  primaryText:    string;
  primaryMuted:   string;
  // Accent
  accent:         string;
  accentLight:    string;
  // Surfaces
  surface:        string;
  surfaceRaised:  string;
  surfaceOverlay: string;
  // Textes
  content:        string;
  contentMuted:   string;
  contentSubtle:  string;
  contentInverted: string;
  // Bordures
  border:         string;
  borderStrong:   string;
  // États sémantiques
  success:        string;
  successLight:   string;
  warning:        string;
  warningLight:   string;
  danger:         string;
  dangerLight:    string;
  // Sidebar / navigation
  sidebarBg:      string;
  sidebarText:    string;
  sidebarActive:  string;
  sidebarHover:   string;
  sidebarBorder:  string;
}

export interface Theme {
  slug:        string;
  nom:         string;
  description: string;
  couleurs:    ThemeColors;
}

// ── Thème 1 : Indigo (défaut) ────────────────────────────────────────────────
const indigoTheme: Theme = {
  slug: 'indigo',
  nom: 'Indigo',
  description: 'Thème par défaut — élégant et professionnel',
  couleurs: {
    primary: '79 70 229', primaryHover: '67 56 202', primaryLight: '238 242 255',
    primaryDark: '55 48 163', primaryText: '99 102 241', primaryMuted: '165 180 252',
    accent: '99 102 241', accentLight: '224 231 255',
    surface: '255 255 255', surfaceRaised: '248 250 252', surfaceOverlay: '241 245 249',
    content: '15 23 42', contentMuted: '71 85 105', contentSubtle: '148 163 184',
    contentInverted: '255 255 255',
    border: '226 232 240', borderStrong: '148 163 184',
    success: '16 185 129', successLight: '209 250 229',
    warning: '245 158 11', warningLight: '254 243 199',
    danger: '239 68 68', dangerLight: '254 226 226',
    sidebarBg: '15 23 42', sidebarText: '148 163 184', sidebarActive: '79 70 229',
    sidebarHover: '30 41 59', sidebarBorder: '30 41 59',
  },
};

// ── Thème 2 : Émeraude ───────────────────────────────────────────────────────
const emeraudeTheme: Theme = {
  slug: 'emeraude',
  nom: 'Émeraude',
  description: 'Verdoyant et apaisant',
  couleurs: {
    primary: '5 150 105', primaryHover: '4 120 87', primaryLight: '209 250 229',
    primaryDark: '6 95 70', primaryText: '16 185 129', primaryMuted: '110 231 183',
    accent: '16 185 129', accentLight: '167 243 208',
    surface: '255 255 255', surfaceRaised: '249 250 251', surfaceOverlay: '243 244 246',
    content: '17 24 39', contentMuted: '75 85 99', contentSubtle: '156 163 175',
    contentInverted: '255 255 255',
    border: '229 231 235', borderStrong: '156 163 175',
    success: '16 185 129', successLight: '209 250 229',
    warning: '245 158 11', warningLight: '254 243 199',
    danger: '239 68 68', dangerLight: '254 226 226',
    sidebarBg: '6 78 59', sidebarText: '167 243 208', sidebarActive: '52 211 153',
    sidebarHover: '4 120 87', sidebarBorder: '4 120 87',
  },
};

// ── Thème 3 : Rose ───────────────────────────────────────────────────────────
const roseTheme: Theme = {
  slug: 'rose',
  nom: 'Rose',
  description: 'Chaleureux et dynamique',
  couleurs: {
    primary: '225 29 72', primaryHover: '190 18 60', primaryLight: '255 228 230',
    primaryDark: '159 18 57', primaryText: '244 63 94', primaryMuted: '251 113 133',
    accent: '244 63 94', accentLight: '255 241 242',
    surface: '255 255 255', surfaceRaised: '255 249 250', surfaceOverlay: '255 241 242',
    content: '15 23 42', contentMuted: '71 85 105', contentSubtle: '148 163 184',
    contentInverted: '255 255 255',
    border: '226 232 240', borderStrong: '148 163 184',
    success: '16 185 129', successLight: '209 250 229',
    warning: '245 158 11', warningLight: '254 243 199',
    danger: '239 68 68', dangerLight: '254 226 226',
    sidebarBg: '136 19 55', sidebarText: '251 207 232', sidebarActive: '244 63 94',
    sidebarHover: '159 18 57', sidebarBorder: '159 18 57',
  },
};

// ── Thème 4 : Ambre ──────────────────────────────────────────────────────────
const ambreTheme: Theme = {
  slug: 'ambre',
  nom: 'Ambre',
  description: 'Chaleur et énergie',
  couleurs: {
    primary: '217 119 6', primaryHover: '180 83 9', primaryLight: '254 243 199',
    primaryDark: '146 64 14', primaryText: '245 158 11', primaryMuted: '252 211 77',
    accent: '245 158 11', accentLight: '254 249 195',
    surface: '255 255 255', surfaceRaised: '255 251 235', surfaceOverlay: '254 243 199',
    content: '28 25 23', contentMuted: '87 83 78', contentSubtle: '168 162 158',
    contentInverted: '255 255 255',
    border: '231 229 228', borderStrong: '168 162 158',
    success: '16 185 129', successLight: '209 250 229',
    warning: '245 158 11', warningLight: '254 243 199',
    danger: '239 68 68', dangerLight: '254 226 226',
    sidebarBg: '120 53 15', sidebarText: '253 230 138', sidebarActive: '245 158 11',
    sidebarHover: '146 64 14', sidebarBorder: '146 64 14',
  },
};

// ── Thème 5 : Violet ─────────────────────────────────────────────────────────
const violetTheme: Theme = {
  slug: 'violet',
  nom: 'Violet',
  description: 'Créatif et moderne',
  couleurs: {
    primary: '124 58 237', primaryHover: '109 40 217', primaryLight: '237 233 254',
    primaryDark: '91 33 182', primaryText: '139 92 246', primaryMuted: '196 181 253',
    accent: '139 92 246', accentLight: '245 243 255',
    surface: '255 255 255', surfaceRaised: '250 245 255', surfaceOverlay: '245 243 255',
    content: '15 23 42', contentMuted: '71 85 105', contentSubtle: '148 163 184',
    contentInverted: '255 255 255',
    border: '226 232 240', borderStrong: '148 163 184',
    success: '16 185 129', successLight: '209 250 229',
    warning: '245 158 11', warningLight: '254 243 199',
    danger: '239 68 68', dangerLight: '254 226 226',
    sidebarBg: '46 16 101', sidebarText: '221 214 254', sidebarActive: '139 92 246',
    sidebarHover: '91 33 182', sidebarBorder: '91 33 182',
  },
};

// ── Thème 6 : Ardoise sombre ─────────────────────────────────────────────────
const ardoiseTheme: Theme = {
  slug: 'ardoise',
  nom: 'Ardoise',
  description: 'Mode sombre élégant',
  couleurs: {
    primary: '56 189 248', primaryHover: '14 165 233', primaryLight: '7 89 133',
    primaryDark: '2 132 199', primaryText: '125 211 252', primaryMuted: '186 230 253',
    accent: '14 165 233', accentLight: '7 89 133',
    surface: '30 41 59', surfaceRaised: '15 23 42', surfaceOverlay: '51 65 85',
    content: '248 250 252', contentMuted: '203 213 225', contentSubtle: '148 163 184',
    contentInverted: '15 23 42',
    border: '51 65 85', borderStrong: '71 85 105',
    success: '52 211 153', successLight: '6 78 59',
    warning: '251 191 36', warningLight: '120 53 15',
    danger: '248 113 113', dangerLight: '127 29 29',
    sidebarBg: '2 6 23', sidebarText: '148 163 184', sidebarActive: '56 189 248',
    sidebarHover: '15 23 42', sidebarBorder: '15 23 42',
  },
};

// ── Thème 7 : Caritas ────────────────────────────────────────────────────────
const caritasTheme: Theme = {
  slug: 'caritas',
  nom: 'Caritas',
  description: 'Rouge Caritas — compassion et dignité humaine',
  couleurs: {
    primary: '177 29 35', primaryHover: '139 33 19', primaryLight: '235 227 215',
    primaryDark: '110 15 15', primaryText: '177 29 35', primaryMuted: '202 197 184',
    accent: '96 180 205', accentLight: '220 240 247',
    surface: '255 255 255', surfaceRaised: '253 250 246', surfaceOverlay: '235 227 215',
    content: '28 25 23', contentMuted: '87 83 78', contentSubtle: '168 162 158',
    contentInverted: '255 255 255',
    border: '202 197 184', borderStrong: '168 162 158',
    success: '87 176 109', successLight: '209 235 215',
    warning: '230 137 34', warningLight: '252 230 190',
    danger: '177 29 35', dangerLight: '227 208 204',
    sidebarBg: '139 33 19', sidebarText: '202 197 184', sidebarActive: '236 175 59',
    sidebarHover: '177 29 35', sidebarBorder: '110 15 15',
  },
};

export const THEMES_PREDEFINIS: Theme[] = [
  indigoTheme, emeraudeTheme, roseTheme, ambreTheme, violetTheme, ardoiseTheme, caritasTheme,
];

export const THEME_DEFAUT = indigoTheme;
